import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import {
    setViewToFullscreen,
    setViewToMinimized,
    setViewToSideBar,
} from 'flow/actions/meeting-actions';
import ContextMenu from 'flow/components/ContextMenu';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import { DisplayMode } from 'flow/utils/rtc-utils';
import { selectActiveMeetingView } from 'flow/selectors/meeting-selectors';

type MeetingViewMenuProps = {
    className?: string;
};

type ViewTriggerIcon =
    | 'fullscreen_inverted'
    | 'right_panel_inverted'
    | 'bottombar_inverted';

// The trigger glyph mirrors the active view, so the button doubles as a
// status indicator for the current layout.
const VIEW_TRIGGER_ICON: Record<string, ViewTriggerIcon> = {
    [DisplayMode.FULLSCREEN.key]: 'fullscreen_inverted',
    [DisplayMode.THUMBNAIL.key]: 'right_panel_inverted',
    [DisplayMode.MINIMIZED.key]: 'bottombar_inverted',
};

const MeetingViewMenu = ({ className }: MeetingViewMenuProps) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const meetingView = useAppSelector(selectActiveMeetingView);
    const menuIcon = VIEW_TRIGGER_ICON[meetingView] ?? 'bottombar_inverted';

    return (
        <ContextMenu
            triggerLabel={i18next.t('videoconference.view_menu')}
            preventOverflow={true}
            skipPortal={true}
            icon={<Icon iconKey={menuIcon} />}
            className={twMerge('relative flex', className)}
            menuClassName="no-dark !bg-gray-800 py-4 text-white"
            dataTestId="meeting-view-menu"
        >
            <MenuItem
                dataTestId="meeting-view-menu-fullscreen_inverted"
                onClick={() => {
                    dispatch(setViewToFullscreen());
                }}
                className="gap-2 hover:bg-gray-900"
            >
                <Icon iconKey="fullscreen_inverted" />
                {i18next.t('videoconference.header.fullscreen')}
                {meetingView === DisplayMode.FULLSCREEN.key ? (
                    <Icon iconKey="done" />
                ) : null}
            </MenuItem>
            <MenuItem
                dataTestId="meeting-view-menu-right_panel_inverted"
                onClick={() => {
                    dispatch(setViewToSideBar());
                }}
                className="gap-2 hover:bg-gray-900"
            >
                <Icon iconKey="right_panel_inverted" />
                {i18next.t('videoconference.header.rightpanel')}
                {meetingView === DisplayMode.THUMBNAIL.key ? (
                    <Icon iconKey="done" />
                ) : null}
            </MenuItem>
            <MenuItem
                dataTestId="meeting-view-menu-bottombar_inverted"
                onClick={() => {
                    dispatch(setViewToMinimized());
                }}
                className="gap-2 hover:bg-gray-900"
            >
                <Icon iconKey="bottombar_inverted" />
                {i18next.t('videoconference.header.bottombar')}
                {meetingView === DisplayMode.MINIMIZED.key ? (
                    <Icon iconKey="done" />
                ) : null}
            </MenuItem>
        </ContextMenu>
    );
};

export default MeetingViewMenu;
